import Vue from "vue";
import axios from "axios";
import { SessionStorage } from 'quasar'
import { TokenManager } from '@cenora-llc/cenora-internal-communication-library'

/**
 * Get token using TokenManager (postMessage to parent) with SessionStorage fallback
 * Priority: TokenManager cache → SessionStorage (fallback)
 */
const getToken = () => {
  // PRIORITY 1: TokenManager cache (synced from parent via postMessage)
  if (TokenManager) {
    const tokenManagerToken = TokenManager.getCachedToken();
    if (tokenManagerToken) {
      return tokenManagerToken;
    }
  }

  // PRIORITY 2: SessionStorage fallback
  return SessionStorage.getItem('token');
}

const getAuthHeaders = () => {
  const headers = {};
  const accessToken = getToken();

  if (accessToken) {
    headers['access_token'] = accessToken;
  }

  return headers;
}

// request interceptor
axios.interceptors.request.use(
  config => {
    const authHeaders = getAuthHeaders();
    config.url = config.url;
    config.headers = { ...config.headers, ...authHeaders };
    return config;
  },
  error => {
    return Promise.reject(error);
  }
);

// response interceptor - Handle 401 Unauthorized errors with retry
axios.interceptors.response.use(
  response => {
    return response.data.body ? response.data.body : response.data;
  },
  async function (error) {
    const originalRequest = error.config;

    // Handle 401 Unauthorized errors with retry mechanism
    if (error.response && error.response.status === 401 && !originalRequest._retry) {
      console.warn('[Axios] First attempt failed with 401, requesting fresh token from parent...');

      // Mark request as retried to prevent infinite loops
      originalRequest._retry = true;

      try {
        // Clear cached token and request fresh one from parent (if TokenManager available)
        if (TokenManager) {
          TokenManager.clearToken();
          const freshToken = await TokenManager.getToken(true);

          if (freshToken) {
            console.log('[Axios] Retrying request with fresh token from parent...');
            // Update the authorization header with fresh token
            originalRequest.headers['access_token'] = freshToken;
            // Sync to SessionStorage for consistency
            SessionStorage.set('token', freshToken);
            // Retry the request
            return axios(originalRequest);
          } else {
            console.error('[Axios] No fresh token available from parent');
          }
        } else {
          console.warn('[Axios] TokenManager not available, cannot retry with fresh token');
        }

        // Clear session and redirect to logout if still no token
        SessionStorage.clear();
        // Optionally redirect: window.location = "/logout";
      } catch (refreshError) {
        console.error('[Axios] Token refresh failed:', refreshError);
        SessionStorage.clear();
      }
    }

    // Handle other error status codes
    if (error.response) {
      switch (error.response.status) {
        case 400:
          console.log("Something happened: ", error.response.status);
          break;
        case 500:
          console.log("Something happened: ", error.response.status);
          break;
        default:
          console.log("Something happened: ", error.response.status);
          break;
      }
    }

    // return Error object with Promise
    return Promise.reject(error);
  }
);

Vue.prototype.$axios = axios;



